import React, { useCallback, useState } from 'react';

import OvertimeConfirmationModal from '@/routes/Agency/CreateGigOrder/JobEditor/Steps/StaffingStep/OvertimeConfirmationModal';
import { GAEvent } from '@/constants/gaevents';
import {
  JobsAPI,
  OvertimeProjection,
  OvertimeProjectionResult,
} from '@/services/jobsAPI';
import useAnalytics from '@/util/analytics';
import {
  trackOvertimeModalConfirmed,
  trackOvertimeModalDismissed,
  trackOvertimeModalShown,
  trackOvertimeProjectionUnavailable,
} from '@/util/vexo';
import { workerName } from '@/util/worker';

type GateMode = 'hire' | 'invite' | 'remove';

type CheckOptions = {
  // Skip the gate entirely (used on rehire / already-offered overrides where
  // the admin has already accepted a prior confirmation modal).
  force?: boolean;
  // If the caller already has the projection (e.g. valid_workers decorates it
  // on the worker object), pass it here to skip the REST roundtrip. Pass
  // `null` to mean "I checked and there's no applicable projection".
  prefetchedProjection?: OvertimeProjection | null;
  // For surfaces that don't have a Job id yet (the order-creation wizard sees
  // schedules in memory before submitting). The hook calls the
  // overtime_projection_from_shifts endpoint instead of the per-Job one.
  // Ignored when `prefetchedProjection` is also provided.
  //
  // GRAVY-10073 break-aware: each shift may carry `mandatory_break_minutes`
  // so the projection subtracts break time when computing per-week
  // overtime. Missing field defaults to 0 server-side.
  proposedShifts?: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>;
};

// Surfaces use this to instrument `worker_invited` / `worker_hired` with
// the same OT context the modal saw. `triggered: false` means the modal
// did not show (either the gate short-circuited via remove/force, the
// projection was nil, or projected hours were under 40).
export type OvertimeOutcome = {
  triggered: boolean;
  worker_current_hours: number | null;
  overtime_hours: number | null;
  already_in_overtime: boolean;
  // false when the action proceeded past the fail-closed "projection
  // unavailable" warning (we couldn't compute the worker's hours). Lets
  // callers record honestly that the hire/invite went through without a
  // verified OT number. Benign proceeds and real warnings are `true`.
  projection_available: boolean;
};

const NULL_OUTCOME: OvertimeOutcome = {
  triggered: false,
  worker_current_hours: null,
  overtime_hours: null,
  already_in_overtime: false,
  projection_available: true,
};

// Sum hours across the impacted weeks of a projection. The projection
// itself reports per-week numbers; the Vexo events want totals so a single
// shift event captures the cumulative picture.
const summarizeProjection = (
  projection: OvertimeProjection,
): {
  worker_current_hours: number;
  shift_hours: number;
  projected_total_hours: number;
  overtime_hours: number;
  already_in_overtime: boolean;
} => {
  const weeks = projection.weeks ?? [];
  const worker_current_hours = weeks.reduce(
    (sum, w) => sum + (w.current_hours ?? 0),
    0,
  );
  const projected_total_hours = weeks.reduce(
    (sum, w) => sum + (w.projected_hours ?? 0),
    0,
  );
  const overtime_hours = weeks.reduce((sum, w) => sum + (w.ot_hours ?? 0), 0);
  const already_in_overtime = weeks.some((w) => (w.current_hours ?? 0) > 40);
  return {
    worker_current_hours: Number(worker_current_hours.toFixed(2)),
    shift_hours: Number(
      (projected_total_hours - worker_current_hours).toFixed(2),
    ),
    projected_total_hours: Number(projected_total_hours.toFixed(2)),
    overtime_hours: Number(overtime_hours.toFixed(2)),
    already_in_overtime,
  };
};

// Accepts any object that has at least an `id`. We type as Record<string, any>
// so the hook composes with the various worker shapes used across the app
// (REST autocomplete, GraphQL fragments, decorated valid_workers rows, ...).
type WorkerLike = Record<string, any>;

type ProjectionSummary = ReturnType<typeof summarizeProjection>;

type Pending = {
  jobId: string | number | null;
  worker: WorkerLike;
  // Absent on the `unavailable` arm — there is no projection to show when
  // the request failed.
  projection?: OvertimeProjection;
  // Computed once at gate-fire time so the proceed/cancel callbacks reuse
  // the same per-week summary the modal was shown with. Absent when
  // `unavailable`.
  summary?: ProjectionSummary;
  // true when the modal is the fail-closed "projection unavailable" warning
  // rather than a real over-40 projection.
  unavailable: boolean;
  mode: GateMode;
  onProceed: (outcome: OvertimeOutcome) => void;
  onCancel?: () => void;
};

/**
 * useOvertimeGate centralises the W2 over-40 confirmation flow shared by every
 * invite/hire entry point in tenant-admin. Callers wrap their submit in
 * `check(jobId, worker, mode, onProceed)`. If the projection trips the 40h
 * threshold for a W2 worker, the hook renders OvertimeConfirmationModal and
 * runs `onProceed` only after the admin confirms. Otherwise `onProceed` runs
 * synchronously.
 *
 * `onProceed` receives an `OvertimeOutcome` so callers can fire `worker_invited`
 * / `worker_hired` Vexo events with the same OT context the modal saw.
 *
 * Render `{modal}` once near the consumer's existing modal/portal output.
 *
 * Concurrency: the hook owns a single `pending` slot, so two parallel `check`
 * calls would overwrite each other. Today's callers either serialize via a
 * per-job `await new Promise(...)` loop (InviteAndHireModal, WorkerStaffingModel)
 * or gate behind an `isLoading` submit button (NewOfferModal, AddWorkerModal),
 * so this is safe. If a future caller fans out parallel checks, swap the slot
 * for a queue.
 */
export const useOvertimeGate = () => {
  const { logEvent } = useAnalytics();
  const [pending, setPending] = useState<Pending | null>(null);

  const check = useCallback(
    async (
      jobId: string | number | null,
      worker: WorkerLike,
      mode: GateMode,
      onProceed: (outcome: OvertimeOutcome) => void,
      opts: CheckOptions & { onCancel?: () => void } = {},
    ) => {
      if (mode === 'remove' || opts.force) {
        onProceed(NULL_OUTCOME);
        return;
      }
      let result: OvertimeProjectionResult;
      if (opts.prefetchedProjection !== undefined) {
        // Caller-supplied value is never a failed request: a projection →
        // ok, null → not applicable. (No consumer passes this today, but
        // the path is kept working; a future caller that decorates
        // valid_workers must pre-resolve its own errors.)
        result = opts.prefetchedProjection
          ? { status: 'ok', projection: opts.prefetchedProjection }
          : { status: 'not_applicable' };
      } else if (jobId != null) {
        result = await JobsAPI.overtimeProjection(jobId, worker.id);
      } else if (opts.proposedShifts && opts.proposedShifts.length > 0) {
        result = await JobsAPI.overtimeProjectionFromShifts(
          worker.id,
          opts.proposedShifts,
        );
      } else {
        result = { status: 'not_applicable' };
      }

      // FAIL CLOSED: the projection couldn't be computed (network error,
      // HTTP 500, or a blocked request). Previously this silently proceeded,
      // letting an admin hire a W2 worker into overtime with no warning.
      // Surface the conservative "unavailable" confirmation and wait for an
      // explicit decision instead of running onProceed.
      if (result.status === 'error') {
        trackOvertimeProjectionUnavailable({
          job_id: jobId,
          worker_id: worker.id,
          action_type: mode === 'hire' ? 'hire' : 'invite',
        });
        setPending({
          jobId,
          worker,
          unavailable: true,
          mode,
          onProceed,
          onCancel: opts.onCancel,
        });
        return;
      }

      // 1099 worker or backend `applicable:false`: no OT obligation, proceed
      // silently. This is the single most important behavior not to regress.
      if (result.status === 'not_applicable') {
        onProceed(NULL_OUTCOME);
        return;
      }

      // Overtime is evaluated per payroll week, not per order. The backend
      // reports `exceeds_40` only when *some single week's* `current + job`
      // total crosses the 40h threshold (US/Eastern, Monday 04:00 boundary).
      // Trust that signal directly: a multi-week order whose total exceeds
      // 40 but whose individual weeks stay under (e.g. 45h split 27h/18h
      // across a payroll-week boundary) must NOT fire the modal.
      const projection = result.projection;
      if (projection.exceeds_40) {
        const summary = summarizeProjection(projection);
        logEvent(GAEvent.OvertimeWarningShown, worker.id, {
          workerId: worker.id,
        });
        // Vexo `overtime_modal_shown` fires alongside the GA event so the
        // worker-actions funnel sees the same shape on both sinks.
        trackOvertimeModalShown({
          job_id: jobId,
          worker_id: worker.id,
          worker_current_hours: summary.worker_current_hours,
          shift_hours: summary.shift_hours,
          projected_total_hours: summary.projected_total_hours,
          overtime_hours: summary.overtime_hours,
          action_type: mode === 'hire' ? 'hire' : 'invite',
          already_in_overtime: summary.already_in_overtime,
        });
        setPending({
          jobId,
          worker,
          projection,
          summary,
          unavailable: false,
          mode,
          onProceed,
          onCancel: opts.onCancel,
        });
        return;
      }
      onProceed(NULL_OUTCOME);
    },
    [logEvent],
  );

  const modal = pending ? (
    <OvertimeConfirmationModal
      isOpen
      unavailable={pending.unavailable}
      workerName={workerName(pending.worker) || 'this worker'}
      projection={pending.projection}
      onProceed={() => {
        logEvent(GAEvent.OvertimeWarningConfirmed, pending.worker.id, {
          workerId: pending.worker.id,
        });
        // Fail-closed confirm: no projection numbers exist, so record the
        // proceed with null hours and `projection_available: false`.
        if (pending.unavailable) {
          trackOvertimeModalConfirmed({
            job_id: pending.jobId,
            worker_id: pending.worker.id,
            overtime_hours: 0,
            action_type: pending.mode === 'hire' ? 'hire' : 'invite',
            already_in_overtime: false,
            projection_available: false,
          });
          const fn = pending.onProceed;
          setPending(null);
          fn({
            triggered: true,
            worker_current_hours: null,
            overtime_hours: null,
            already_in_overtime: false,
            projection_available: false,
          });
          return;
        }
        const summary = pending.summary!;
        trackOvertimeModalConfirmed({
          job_id: pending.jobId,
          worker_id: pending.worker.id,
          overtime_hours: summary.overtime_hours,
          action_type: pending.mode === 'hire' ? 'hire' : 'invite',
          already_in_overtime: summary.already_in_overtime,
        });
        const fn = pending.onProceed;
        setPending(null);
        fn({
          triggered: true,
          worker_current_hours: summary.worker_current_hours,
          overtime_hours: summary.overtime_hours,
          already_in_overtime: summary.already_in_overtime,
          projection_available: true,
        });
      }}
      onCancel={() => {
        logEvent(GAEvent.OvertimeWarningCancelled, pending.worker.id, {
          workerId: pending.worker.id,
        });
        // OvertimeConfirmationModal only exposes a single onCancel today;
        // future product spec may split "Go back" footer button from the
        // "×" close button. Default to 'go_back' for now.
        if (pending.unavailable) {
          trackOvertimeModalDismissed({
            job_id: pending.jobId,
            worker_id: pending.worker.id,
            overtime_hours: 0,
            dismiss_method: 'go_back',
            already_in_overtime: false,
            projection_available: false,
          });
          const fn = pending.onCancel;
          setPending(null);
          if (fn) fn();
          return;
        }
        const summary = pending.summary!;
        trackOvertimeModalDismissed({
          job_id: pending.jobId,
          worker_id: pending.worker.id,
          overtime_hours: summary.overtime_hours,
          dismiss_method: 'go_back',
          already_in_overtime: summary.already_in_overtime,
        });
        const fn = pending.onCancel;
        setPending(null);
        if (fn) fn();
      }}
    />
  ) : null;

  return { check, modal };
};

export default useOvertimeGate;
